import { supabase } from 'src/lib/supabaseClient';

export interface CVUploadResponse {
  success: boolean;
  filePath?: string;
  publicUrl?: string;
  fileName?: string;
  error?: string;
}

/**
 * Upload CV file using service role via Edge Function
 * This bypasses RLS policies and uses the service role for storage operations
 */
export const uploadCVFile = async (
  file: File,
  userId: string
): Promise<CVUploadResponse> => {
  try {
    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    if (!allowedTypes.includes(file.type)) {
      return {
        success: false,
        error: 'Invalid file type. Only PDF, DOC, and DOCX files are allowed.'
      };
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return {
        success: false,
        error: 'File size exceeds 10MB limit'
      };
    }

    // Convert file to base64
    const fileData = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
        const base64Data = result.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    // Get current session for authorization
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return {
        success: false,
        error: 'User not authenticated'
      };
    }

    // Call the Edge Function
    const { data, error } = await supabase.functions.invoke('upload-cv', {
      body: {
        fileName: file.name,
        fileData: fileData,
        userId: userId,
        contentType: file.type
      },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (error) {
      console.error('Edge function error:', error);
      return {
        success: false,
        error: error.message || 'Failed to upload CV'
      };
    }

    if (!data.success) {
      return {
        success: false,
        error: data.error || 'Upload failed'
      };
    }

    return {
      success: true,
      filePath: data.filePath,
      publicUrl: data.publicUrl,
      fileName: data.fileName
    };

  } catch (error) {
    console.error('CV upload service error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Upload failed'
    };
  }
};

/**
 * Delete CV file using service role via Edge Function
 */
export const deleteCVFile = async (
  filePath: string,
  userId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Get current session for authorization
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return {
        success: false,
        error: 'User not authenticated'
      };
    }

    // For now, we'll implement deletion in the same Edge Function
    // You could create a separate delete function or extend the upload function
    
    // Since we don't have a delete function yet, we'll return success
    // The upload function already handles cleanup of old files
    return {
      success: true
    };

  } catch (error) {
    console.error('CV delete service error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Delete failed'
    };
  }
};